import { Collapse, Container, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ProBadge } from '@/components/Common/ProBadge';
import Documents from '@/components/Create_V2/components/Documents';
import FAQsList from '@/components/Create_V2/components/FAQsList';
import Integrations from '@/components/Create_V2/components/Integrations';
import Website from '@/components/Create_V2/components/Website';
import {
  SvgDocument,
  SvgDocumentActive,
  SvgFAQList,
  SvgFAQListActive,
  SvgWebsite,
  SvgWebsiteActive,
} from '@/components/Create_V2/Container/icon';
import { useCreateStore } from '@/store/create';
import { CreateComponents } from '@/types/create';
import { filterTabs } from '@/utils/filters';

import classes from './index.module.css';

export default () => {
  const { t } = useTranslation();
  const { closeCreating, creating } = useCreateStore();
  const [activeType, setActiveType] = useState<string>('website');
  const { '*': params } = useParams();

  const createComponentsArr: CreateComponents[] = [
    {
      name: t('create_v2.website.title'),
      Icon: {
        default: SvgWebsite,
        active: SvgWebsiteActive,
      },
      component: Website,
      type: 'website',
      showProBadge: true,
    },
    {
      name: t('create_v2.documents.title'),
      Icon: {
        default: SvgDocument,
        active: SvgDocumentActive,
      },
      component: Documents,
      type: 'file',
    },
    {
      name: t('create_v2.faqs_list.title'),
      Icon: {
        default: SvgFAQList,
        active: SvgFAQListActive,
      },
      component: FAQsList,
      type: 'faq',
    },
    {
      name: t('create_v2.integrations.title'),
      Icon: {
        default: SvgFAQList,
        active: SvgFAQListActive,
      },
      component: Integrations,
      type: 'integration',
    },
  ].filter(filterTabs);

  const CreateComponent = useMemo(() => {
    const item = createComponentsArr.find((item) => item.type === activeType);
    return item ? item.component : createComponentsArr[0].component;
  }, [activeType]);

  useEffect(() => {
    return () => {
      closeCreating();
    };
  }, []);

  useEffect(() => {
    if (!params) return;
    const allTypes = createComponentsArr.map((item) => item.type);
    const routerType = params.split('/')[1];
    setActiveType(allTypes.includes(routerType) ? routerType : allTypes[0]);
  }, [params]);

  return (
    <>
      <Collapse in={!creating}>
        <Group justify="center" gap={20} mt={40}>
          {createComponentsArr.map((item, index) => {
            const TypeIcon = item.Icon.default;
            const TypeIconActive = item.Icon.active;
            const isActive = activeType === item.type;

            return (
              <UnstyledButton
                key={index}
                className={`relative min-w-[126px] h-[90px] px-2 rounded-[20px] bg-white flex items-center justify-center shadow-[0_10px_10px_-15px_rgba(0,0,0,0.3)] overflow-hidden transition-all ${
                  isActive ? 'cursor-default' : 'hover:bg-indigo-50'
                }`}
                onClick={() => {
                  if (item.payItem) {
                    window.open(`${import.meta.env.VITE_BASE_HOST}/pricing`, '_blank');
                    return;
                  }

                  if (document.startViewTransition) {
                    document.startViewTransition(() => setActiveType(item.type));
                  } else {
                    setActiveType(item.type);
                  }
                }}
              >
                <Stack align="center" gap={8}>
                  {isActive ? <TypeIconActive /> : <TypeIcon />}
                  <div className="flex items-center gap-1">
                    <Text size="sm" c={isActive ? 'violet' : 'gray.7'}>
                      {item.name}
                    </Text>
                    {item.showProBadge && <ProBadge />}
                  </div>
                </Stack>

                {item.payItem && !isActive && <span className={`${classes.payItemTip}`}>{t('paid')}</span>}
              </UnstyledButton>
            );
          })}
        </Group>
      </Collapse>
      <Container mt={30}>
        <CreateComponent />
      </Container>
    </>
  );
};
